'use client';

import { create } from 'zustand';
import { registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';

type EntryDraft = { attemptId: string; prompt: string; files: File[] };

/** One multimodal draft survives login and changes between discovery sections. */
export const useExploreConsultationDraftStore = create<{
  prompt: string;
  files: File[];
  restoring: boolean;
  entryDraft?: EntryDraft;
}>(() => ({ prompt: '', files: [], restoring: false }));

/** Keep the accepted draft identity across SPA remounts without persisting files twice. */
export function rememberExploreEntryDraft(attemptId: string, prompt: string, files: File[]): void {
  useExploreConsultationDraftStore.setState({ entryDraft: { attemptId, prompt, files: [...files] } });
}

/** A restored upload may replace its own in-memory draft, never a newer edit. */
export function restoreExploreEntryDraft(attemptId: string, prompt: string, files: File[], controlledPrompt?: string): boolean {
  const current = useExploreConsultationDraftStore.getState();
  const previous = current.entryDraft;
  // Other discovery modes can edit the parent draft without updating this store.
  if (controlledPrompt !== undefined && controlledPrompt !== prompt
      && (controlledPrompt.length > 0 || previous)) return false;
  if ((current.prompt || current.files.length) && !(previous?.attemptId === attemptId
      && current.prompt === previous.prompt && current.files.length === previous.files.length
      && current.files.every((file, index) => file === previous.files[index]))) return false;
  useExploreConsultationDraftStore.setState({ prompt, files,
    entryDraft: { attemptId, prompt, files: [...files] } });
  return true;
}

registerAuthScopedCacheReset('explore-consultation-draft', () => {
  useExploreConsultationDraftStore.setState({prompt: '', files: [], restoring: false, entryDraft: undefined});
});
